import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApronService } from '../apron.service';
import { ActivatedRoute, Router } from "@angular/router";
import { ApronDto, ApproveOperationDto, MarkPendingDto } from "@up/data";
import { Subscription, timer } from "rxjs";
import { switchMap, takeWhile } from "rxjs/operators";

interface ShiftData {
  transactionId: string;
  employeeName: string;
  floatAmount: number;
  cashFromSales: number;
  timestamp: Date;
  avatarUrl?: string;
}

type AppState = 'PIN' | 'FLOAT_DETAILS' | 'COUNT_INCORRECT' | 'QR_CODE' | 'HANDOVER_COMPLETE';

@Component({
  selector: 'up-end-shift',
  templateUrl: './end-shift.component.html',
  styleUrls: ['./end-shift.component.scss']
})
export class EndShiftComponent implements OnInit, OnDestroy {

  // --- STATE ---
  currentState: AppState = 'PIN'; // Start at PIN

  // Notification Logic
  showNotification = false;
  notificationTitle = '';
  notificationMessage = '';
  notificationType: 'error' | 'warning' | 'info' = 'error';

  // Data Logic
  data: ShiftData | null = null;
  isLoading = true;
  showModal = false;
  apron: ApronDto;
  apronId: string;
  costCentreId: number;
  managerEmployeeId: number;
  subscription: Subscription;

  // Incorrect Count Logic
  isIncorrectCountConfirmed = false;
  countedAmount = 0;
  comment = '';

  constructor(private apronService: ApronService,
    private route: ActivatedRoute,
    private router: Router) {
    this.apronId = this.route.snapshot.paramMap.get('apronId');
  }

  ngOnInit() {
    this.fetchData();
    this.checkAuth();
  }

  // --- 1. PIN & AUTHENTICATION ---
  checkAuth() {
    const storedAuth = localStorage.getItem('pin_auth');
    const storedManagerId = localStorage.getItem('manager_employee_id');
    if (storedAuth === 'true' && storedManagerId) {
      this.managerEmployeeId = Number(storedManagerId);
      this.currentState = 'FLOAT_DETAILS';
      this.markAsPending();
    } else {
      this.currentState = 'PIN';
    }
  }

  onPinValidated(managerId: number) {
    this.managerEmployeeId = managerId;
    this.currentState = 'FLOAT_DETAILS';
    this.markAsPending();
  }

  private markAsPending() {
    const markPendingDto: MarkPendingDto = {
      managerEmployeeId: this.managerEmployeeId
    };

    this.apronService.markEndPending(Number(this.apronId), markPendingDto).subscribe(
      () => {
        this.currentState = 'FLOAT_DETAILS';
      },
      (error) => {
        console.log(error);
        const status = error?.statusCode;

        if (status === 409) {
          // Conflict - Status already approved or not in the expected state
          this.showNotificationMessage(
            'Shift End Process Already Initiated',
            'This shift end process has already started and may have been completed already.',
            'warning'
          );
        }
      }
    );
  }

  // --- 2. DATA & MODAL ---
  get totalAmount(): number {
    return this.data ? this.data.floatAmount + this.data.cashFromSales : 0;
  }

  fetchData() {
    this.isLoading = true;
    this.apronService.getById(this.apronId).subscribe(
      async (apron) => {
        this.isLoading = false;
        this.apron = apron;
        if (apron.status === 'end-shift approved') {
          this.currentState = 'QR_CODE';
          this.checkIfAcknowledged();
        }
        if (apron.status === 'end-shift acknowledged') {
          this.currentState = 'HANDOVER_COMPLETE';
          this.isIncorrectCountConfirmed = apron.countedAmount != 0;
          this.countedAmount = apron.countedAmount;
        }
        this.data = {
          transactionId: apron.id.toString(),
          employeeName: apron.operator?.firstName + ' ' + apron.operator?.lastName,
          floatAmount: apron.expectedAmount,
          avatarUrl: apron.operator.imageUrl,
          cashFromSales: apron.cashFromSalesAmount,
          timestamp: apron.modifiedDate
        }

        this.costCentreId = apron.merchant?.costCentreId;
      },
      (error) => {
        this.isLoading = false;
        console.log(error);
        this.router.navigate(['/apron-error']);
        return;
      }
    );
  }

  countIncorrect() {
    this.currentState = 'COUNT_INCORRECT';
  }

  get amountDifference(): number {
    return this.countedAmount - this.totalAmount;
  }

  // --- 3. COUNT INCORRECT HANDLERS ---
  onCountIncorrectConfirmed(amount: number) {
    this.countedAmount = amount;
    this.isIncorrectCountConfirmed = this.amountDifference !== 0.00;
    this.currentState = 'FLOAT_DETAILS';
  }

  onCountIncorrectCancelled() {
    this.currentState = 'FLOAT_DETAILS';
  }

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  showNotificationMessage(title: string, message: string, type: 'error' | 'warning' | 'info' = 'error') {
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.notificationType = type;
    this.showNotification = true;

    // Auto-hide after 10 seconds
    setTimeout(() => {
      this.hideNotification();
    }, 10000);
  }

  hideNotification() {
    this.showNotification = false;
    this.notificationTitle = '';
    this.notificationMessage = '';
  }

  checkIfAcknowledged() {
    this.subscription = timer(0, 5000).pipe(
      switchMap(() => this.apronService.getById(this.apronId)),
      takeWhile(apron => !apron.status.includes('acknowledged'), true)
    ).subscribe((apron) => {
      if (apron.status.includes('acknowledged')) {
        this.currentState = 'HANDOVER_COMPLETE';
      }
    });
  }

  confirmHandover() {
    const approveDto: ApproveOperationDto = {
      managerEmployeeId: this.managerEmployeeId,
      amount: this.isIncorrectCountConfirmed ? this.countedAmount : this.totalAmount,
      note: this.isIncorrectCountConfirmed ? this.comment : 'Ok shift end'
    };

    this.apronService.approveEnd(Number(this.apronId), approveDto).subscribe(
      () => {
        this.closeModal();
        this.currentState = 'QR_CODE';
        this.checkIfAcknowledged();
      },
      (error) => {
        console.log(error);
        this.showNotificationMessage(
          'Failed to approve end shift.',
          'Please try again.',
          'error'
        );
        this.closeModal();

        const status = error?.statusCode;

        if (status === 500 || status === 409) {
          // Internal Server Error - Status issue
          this.showNotificationMessage(
            'Unable to Process Request',
            'The shift status cannot be changed at this time. The End Of Shift process may have already been completed for this employee. Please contact support if this issue persists.',
            'error'
          );
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
